import { enumLabel } from '@immoaktas/i18n';
import { qk, type Period, type YearFile } from '@immoaktas/api-client';
import { actionablePeriod as sharedActionablePeriod, billingReadiness, periodCostRows, type BillingReadiness } from '@immoaktas/shared';
import { api } from '../../../lib/api';
import { readinessText } from '../../../lib/readiness';
import type { PillTone } from '../../../ui';

/**
 * Receipt status of a billing period, shared by Dashboard, Objekte, Haus (side card) and Hausakte so every screen
 * shows the same "fehlt / prüfen" numbers: cost types of the year file (GET /properties/:id/year-file) without a
 * receipt, waiting for review, and how many are complete (not applicable ones excluded).
 */
type TFn = (key: string, opts?: Record<string, unknown>) => string;
type CostRow = YearFile['costs'][number];
export type Gaps = { missing: CostRow[]; review: CostRow[]; done: number; total: number; share: number; readiness: BillingReadiness };

/** The period that needs work now (shared with the server's dashboard inbox). */
export const actionablePeriod: (periods: Period[], propertyId: string, day: string) => Period | undefined = sharedActionablePeriod;

/** `readingsMissing`: meter readings the period still needs (insights), counted in the same readiness measure. */
export function gapsOf(y: YearFile, readingsMissing = 0): Gaps {
  const readiness = billingReadiness(y.costs, { readingsMissing });
  return { missing: y.costs.filter((c) => c.status === 'missing'), review: y.costs.filter((c) => c.status === 'review'), done: readiness.done, total: readiness.total, share: readiness.share, readiness };
}

/**
 * Receipt status of a period: for a calendar year its year file (same cache as the Hausakte); a period over two
 * calendar years (e.g. 01.07.2025 to 30.06.2026) loads both year files and keeps only the months inside the period
 * (`periodCostRows`), so a missing receipt from the first half is not hidden by the second year's file.
 */
/**
 * Per-property queries on overview pages (one request per property): kept for 5 minutes and not refetched on window
 * focus, so a dashboard with many properties does not fire a request storm. Mutations invalidate them as before.
 */
export const PER_PROPERTY = { staleTime: 5 * 60_000, refetchOnWindowFocus: false } as const;

export const gapsQuery = (p: Pick<Period, 'propertyId' | 'from' | 'to'>): { queryKey: readonly unknown[]; queryFn: () => Promise<YearFile> } => {
  const first = Number(p.from.slice(0, 4));
  const last = Number(p.to.slice(0, 4));
  if (first === last && p.from.endsWith('-01-01') && p.to.endsWith('-12-31'))
    return { queryKey: qk.yearFile(p.propertyId, last), queryFn: () => api.properties.yearFile(p.propertyId, last) };
  return {
    queryKey: [...qk.yearFile(p.propertyId, last), 'period', p.from, p.to],
    queryFn: async (): Promise<YearFile> => {
      const years = Array.from({ length: last - first + 1 }, (_, i) => first + i);
      const files = await Promise.all(years.map((y) => api.properties.yearFile(p.propertyId, y)));
      const end = files[files.length - 1]!;
      return { ...end, costs: periodCostRows(files, p.from, p.to) };
    },
  };
};

/** Name of a cost type in gap lists: the category, or the receipt's own name while it waits for review. */
export const gapName = (t: TFn, c: CostRow) => (c.status === 'review' && c.label && c.label !== c.category ? c.label : enumLabel(t, 'costCategory', c.category));

/** Badge of the period that needs work: missing receipts while collecting, else the period status (colour never alone). */
export function periodBadge(t: TFn, period?: Period, gaps?: Gaps): { label: string; tone: PillTone } | null {
  if (!period) return null;
  if (period.needsCorrection) return { label: t('properties.badge.correction'), tone: 'err' };
  if (period.status === 'collecting') {
    // the shared readiness sentence (same words as dashboard, overview, year file and checklist)
    if (gaps && !gaps.readiness.complete) return { label: readinessText(t, gaps.readiness), tone: 'warn' };
    return { label: enumLabel(t, 'periodStatus', 'collecting'), tone: 'neutral' };
  }
  const tone: Record<string, PillTone> = { ready: 'ok', draft: 'neutral', final: 'brand', sent: 'brand', closed: 'neutral' };
  return { label: enumLabel(t, 'periodStatus', period.status), tone: tone[period.status] ?? 'neutral' };
}
